// Phase 1A Phase 9C.3 — useChainProtectedSeries hook.
//
// SELECT-only consumer of public.chain_protected_amounts for the
// authenticated user's chain. Returns the full ascending series so
// the /home hero LineChart can render real protection growth.
//
// Trust contract:
//   - RLS limits SELECT to the caller's own rows (Migration 0030
//     chain_protected_amounts_select_own).
//   - No writes. No RPCs. No backend change. No RLS change.
//   - Preserves null / non-finite values: the pure
//     mapProtectedRowsToSeries transformer drops invalid rows rather
//     than fabricating a value.
//   - No fake series. No extrapolation. No demo points.
//
// The pure transformer is exported for tests (no DOM).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import type { Database } from "../../db/types.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

type Row = Database["public"]["Tables"]["chain_protected_amounts"]["Row"];

export interface ProtectedSeriesPoint {
  weekNumber: number;
  cumulativeProtected: number;
  createdAt: string;
}

export interface UseChainProtectedSeriesState {
  series: ProtectedSeriesPoint[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─── Pure transformer (exported for tests) ───────────────────────

function asNum(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : Number.NaN;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : Number.NaN;
  }
  return Number.NaN;
}

export function mapProtectedRowsToSeries(
  rows: ReadonlyArray<Pick<Row, "week_number" | "cumulative_protected_amount" | "created_at">>,
): ProtectedSeriesPoint[] {
  const out: ProtectedSeriesPoint[] = [];
  // Track latest row per week_number — duplicates collapse, keeping
  // the most recent created_at value (highest fidelity ledger row).
  const byWeek = new Map<number, ProtectedSeriesPoint>();
  for (const r of rows) {
    const wk = typeof r.week_number === "number" ? r.week_number : Number.NaN;
    const amt = asNum(r.cumulative_protected_amount);
    if (!Number.isFinite(wk) || !Number.isFinite(amt)) continue;
    const candidate: ProtectedSeriesPoint = {
      weekNumber: wk,
      cumulativeProtected: amt,
      createdAt: r.created_at ?? "",
    };
    const existing = byWeek.get(wk);
    if (!existing || candidate.createdAt > existing.createdAt) {
      byWeek.set(wk, candidate);
    }
  }
  for (const p of byWeek.values()) out.push(p);
  out.sort((a, b) => a.weekNumber - b.weekNumber);
  return out;
}

// ─── Hook ────────────────────────────────────────────────────────

export function useChainProtectedSeries(chainId: string | null): UseChainProtectedSeriesState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [series, setSeries] = useState<ProtectedSeriesPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user || chainId === null) {
      setSeries([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const r = await supabase
      .from("chain_protected_amounts")
      .select("week_number, cumulative_protected_amount, created_at")
      .eq("chain_id", chainId)
      .order("week_number", { ascending: true });
    if (r.error) {
      setSeries([]);
      setError(r.error.message);
      setLoading(false);
      return;
    }
    setSeries(mapProtectedRowsToSeries(r.data ?? []));
    setLoading(false);
  }, [supabase, user, chainId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseChainProtectedSeriesState>(
    () => ({ series, loading, error, refetch }),
    [series, loading, error, refetch],
  );
}
